import { useEffect, useState, type ReactNode } from "react"
import Button from "@ui/button/Button"
import { AdminModal } from "./AdminModal"
import { Fields, Muted, Row } from "./adminUi"

interface Request {
	title: string
	body?: ReactNode
	confirmLabel?: string
	danger?: boolean
	/** When set, the dialog asks for a value (e.g. a new password) and resolves with it. */
	input?: { label: string; type?: "text" | "password"; minLength?: number; maxLength?: number }
	resolve: (value: string | boolean | null) => void
}

let show: ((request: Request) => void) | null = null

/**
 * Styled replacement for window.confirm. Falls back to the native dialog when no <ConfirmHost/>
 * is mounted (unit tests, isolated renders), so callers never hang.
 */
export const askConfirm = (options: Omit<Request, "resolve" | "input">): Promise<boolean> => {
	// eslint-disable-next-line no-alert
	if (!show) return Promise.resolve(window.confirm([options.title, typeof options.body === "string" ? options.body : ""].filter(Boolean).join("\n\n")))
	return new Promise((resolve) => show!({ ...options, resolve: (value) => resolve(value === true) }))
}

/** Styled replacement for window.prompt; resolves with null on cancel. Password input is masked. */
export const askValue = (options: Omit<Request, "resolve"> & { input: NonNullable<Request["input"]> }): Promise<string | null> => {
	// eslint-disable-next-line no-alert
	if (!show) return Promise.resolve(window.prompt(options.title))
	return new Promise((resolve) => show!({ ...options, resolve: (value) => resolve(typeof value === "string" ? value : null) }))
}

export const ConfirmHost = () => {
	const [request, setRequest] = useState<Request | null>(null)
	const [value, setValue] = useState("")
	useEffect(() => {
		show = (next) => { setValue(""); setRequest(next) }
		return () => { show = null }
	}, [])
	if (!request) return null
	const close = (result: string | boolean | null) => { request.resolve(result); setRequest(null) }
	const tooShort = Boolean(request.input && value.length < (request.input.minLength ?? 1))
	const submit = () => { if (!tooShort) close(request.input ? value : true) }
	return <AdminModal title={request.title} width={460} closeOnBackdrop onClose={() => close(request.input ? null : false)}>
		<form onSubmit={(event) => { event.preventDefault(); submit() }} style={{ display: "grid", gap: 16 }}>
			{request.body && <Muted style={{ fontSize: 14, lineHeight: 1.55 }}>{request.body}</Muted>}
			{request.input && <Fields style={{ gridTemplateColumns: "1fr" }}>
				<label>{request.input.label}<input autoFocus type={request.input.type ?? "text"} autoComplete={request.input.type === "password" ? "new-password" : "off"} placeholder={request.input.minLength ? `At least ${request.input.minLength} characters` : undefined} maxLength={request.input.maxLength} value={value} onChange={(event) => setValue(event.target.value)} /></label>
			</Fields>}
			<Row style={{ justifyContent: "flex-end" }}>
				<Button variant='secondarySmall' onClick={() => close(request.input ? null : false)}>Cancel</Button>
				<Button variant={request.danger ? "dangerSmall" : "primarySmall"} disabled={tooShort} onClick={submit}>{request.confirmLabel ?? "Confirm"}</Button>
			</Row>
		</form>
	</AdminModal>
}
